import React, { useState } from 'react';
import { ArrowLeft, ArrowRight, Mail, FileText, BarChart3, Presentation, Users, Cloud, Share2, Calendar, Bot, CheckCircle, Star, Home } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Link } from 'react-router-dom';
import Breadcrumb from './Breadcrumb';
import Footer from './Footer';

const Office365PresentationPage = () => {
  const [currentSlide, setCurrentSlide] = useState(0);

  // Données des slides
  const slides = [
    // Slide 1 - Présentation générale
    {
      id: 'intro',
      title: 'Bienvenue dans Office 365',
      subtitle: 'La suite bureautique complète de Microsoft',
      content: {
        description: 'Office 365 est une suite d\'outils de productivité conçue pour transformer votre façon de travailler. Découvrez comment chaque outil peut révolutionner votre quotidien professionnel.',
        features: [
          'Suite complète intégrée',
          'Collaboration en temps réel',
          'Accès depuis tous vos appareils', 
          'Sécurité enterprise',
          'Mises à jour automatiques',
          'Stockage cloud inclus'
        ]
      },
      icon: Bot,
      color: 'from-blue-600 to-indigo-600',
      bgColor: 'from-blue-50 to-indigo-50'
    },
    // Slide 2 - Outlook
    {
      id: 'outlook',
      title: 'Outlook',
      subtitle: 'Votre centre de communication professionnel',
      content: {
        description: 'Bien plus qu\'un simple client email, Outlook centralise votre communication avec email, calendrier, contacts et tâches dans une interface unifiée.',
        features: [
          'Gestion avancée des emails',
          'Calendrier intégré et partage',
          'Carnet d\'adresses unifié',
          'Règles et organisation automatique',
          'Intégration Teams et autres outils',
          'Accès mobile et synchronisation'
        ]
      },
      icon: Mail,
      color: 'from-blue-500 to-indigo-600',
      bgColor: 'from-blue-50 to-indigo-50'
    },
    // Slide 3 - Word
    {
      id: 'word',
      title: 'Word',
      subtitle: 'Création de documents professionnels',
      content: {
        description: 'Word révolutionne la création de documents avec des outils de collaboration en temps réel, des modèles professionnels et une intégration parfaite avec le cloud.',
        features: [
          'Co-édition en temps réel',
          'Modèles professionnels intégrés',
          'Révision et commentaires collaboratifs',
          'Référencement automatique',
          'Export multi-formats',
          'Synchronisation cloud automatique'
        ]
      },
      icon: FileText,
      color: 'from-blue-600 to-blue-700',
      bgColor: 'from-blue-50 to-blue-100'
    },
    // Slide 4 - Excel
    {
      id: 'excel',
      title: 'Excel',
      subtitle: 'Analyse de données et tableaux dynamiques',
      content: {
        description: 'Excel transforme vos données en insights avec des outils d\'analyse puissants, des graphiques dynamiques et des fonctionnalités de collaboration avancées.',
        features: [
          'Formules et fonctions avancées',
          'Tableaux croisés dynamiques',
          'Graphiques professionnels',
          'Analyse de données IA',
          'Collaboration temps réel',
          'Automatisation avec macros'
        ]
      },
      icon: BarChart3,
      color: 'from-green-500 to-green-600',
      bgColor: 'from-green-50 to-green-100'
    },
    // Slide 5 - PowerPoint
    {
      id: 'powerpoint',
      title: 'PowerPoint',
      subtitle: 'Présentations impactantes et interactives',
      content: {
        description: 'Créez des présentations professionnelles captivantes avec des outils de design avancés, des animations fluides et des fonctionnalités de collaboration.',
        features: [
          'Templates de design professionnel',
          'Animations et transitions fluides',
          'Collaboration en temps réel',
          'Intégration multimédia avancée',
          'Présentation en ligne et partage',
          'Outils de présentation interactifs'
        ]
      },
      icon: Presentation,
      color: 'from-red-500 to-red-600',
      bgColor: 'from-red-50 to-red-100'
    },
    // Slide 6 - Teams
    {
      id: 'teams',
      title: 'Teams',
      subtitle: 'Plateforme de collaboration d\'équipe',
      content: {
        description: 'Teams réunit chat, vidéoconférences, partage de fichiers et applications dans un espace de travail collaboratif unique.',
        features: [
          'Chat et communication instantanée',
          'Visioconférences HD avec partage écran',
          'Espaces de travail par équipe',
          'Intégration applications métier',
          'Fichiers partagés et co-édition',
          'Téléphonie et conférences audio'
        ]
      },
      icon: Users,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'from-purple-50 to-purple-100'
    },
    // Slide 7 - OneDrive
    {
      id: 'onedrive',
      title: 'OneDrive',
      subtitle: 'Stockage cloud et synchronisation universelle',
      content: {
        description: 'OneDrive synchronise vos fichiers sur tous vos appareils avec partage sécurisé, versioning automatique et collaboration en temps réel.',
        features: [
          'Synchronisation multi-appareils',
          'Partage sécurisé avec permissions',
          'Historique et récupération de versions',
          'Collaboration sur documents',
          'Accès hors ligne automatique',
          'Intégration native Office'
        ]
      },
      icon: Cloud,
      color: 'from-cyan-500 to-cyan-600',
      bgColor: 'from-cyan-50 to-cyan-100'
    },
    // Slide 8 - SharePoint
    {
      id: 'sharepoint',
      title: 'SharePoint',
      subtitle: 'Intranet et gestion documentaire d\'entreprise',
      content: {
        description: 'SharePoint centralise vos documents d\'entreprise avec des sites collaboratifs, des workflows automatisés et une gestion avancée des contenus.',
        features: [
          'Sites d\'équipe collaboratifs',
          'Bibliothèques de documents avancées',
          'Workflows et automatisation',
          'Intranet d\'entreprise personnalisable',
          'Gestion des versions et métadonnées',
          'Intégration complète Office 365'
        ]
      },
      icon: Share2,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'from-orange-50 to-orange-100'
    },
    // Slide 9 - Planner
    {
      id: 'planner',
      title: 'Planner',
      subtitle: 'Gestion de projets et organisation d\'équipe',
      content: {
        description: 'Planner simplifie la gestion de projets avec des tableaux Kanban visuels, des assignations de tâches et un suivi d\'avancement intuitif.',
        features: [
          'Tableaux Kanban visuels',
          'Attribution et suivi des tâches',
          'Calendrier de projet intégré',
          'Collaboration d\'équipe fluide',
          'Rapports d\'avancement automatiques',
          'Intégration Teams et Outlook'
        ]
      },
      icon: Calendar,
      color: 'from-teal-500 to-teal-600',
      bgColor: 'from-teal-50 to-teal-100'
    },
    // Slide 10 - Copilot
    {
      id: 'copilot',
      title: 'Copilot',
      subtitle: 'Intelligence artificielle intégrée',
      content: {
        description: 'Copilot révolutionne votre productivité avec une IA native dans chaque outil Office, transformant votre façon de créer et collaborer.',
        features: [
          'IA intégrée dans chaque outil',
          'Génération de contenu intelligent',
          'Assistance à la rédaction',
          'Analyse de données automatisée',
          'Création de présentations assistée',
          'Productivité décuplée'
        ]
      },
      icon: Bot,
      color: 'from-indigo-500 to-purple-600',
      bgColor: 'from-indigo-50 to-purple-100'
    },
    // Slide 11 - Conclusion et retour
    {
      id: 'conclusion',
      title: 'Prêt à commencer ?',
      subtitle: 'Explorez nos guides détaillés par outil',
      content: {
        description: 'Maintenant que vous connaissez les outils Office 365, découvrez nos guides pratiques détaillés pour maîtriser chaque application.',
        features: [
          'Guides étape par étape',
          'Procédures détaillées',
          'Tips d\'experts',
          'Cas d\'usage concrets',
          'Configuration optimale',
          'Intégrations avancées'
        ]
      },
      icon: Star,
      color: 'from-purple-600 to-pink-600',
      bgColor: 'from-purple-50 to-pink-100',
      isConclusion: true
    }
  ];

  const currentSlideData = slides[currentSlide];
  const IconComponent = currentSlideData.icon;

  const nextSlide = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide(currentSlide + 1);
    }
  };

  const prevSlide = () => {
    if (currentSlide > 0) {
      setCurrentSlide(currentSlide - 1);
    }
  };

  const goToSlide = (index) => {
    setCurrentSlide(index);
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-slate-100">
      {/* Breadcrumb */}
      <div className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Breadcrumb 
            items={[
              { id: 'office365', label: 'Office 365' },
              { id: 'presentation', label: 'Présentation des outils' }
            ]}
          />
        </div>
      </div>

      {/* Hero Banner */}
      <section className={`relative overflow-hidden bg-gradient-to-r ${currentSlideData.color}`}>
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-16">
          <div className="text-center">
            <div className="inline-flex items-center justify-center w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 bg-white bg-opacity-20 backdrop-blur-sm rounded-3xl mb-6 sm:mb-8">
              <IconComponent className="h-10 w-10 sm:h-12 sm:w-12 lg:h-16 lg:w-16 text-white" />
            </div>
            <h1 className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-extrabold text-white mb-4 leading-tight">
              {currentSlideData.title}
            </h1>
            <p className="text-lg sm:text-xl lg:text-2xl text-white text-opacity-90 max-w-3xl mx-auto px-4">
              {currentSlideData.subtitle}
            </p>
          </div>
        </div>
        
        {/* Floating elements - hidden on mobile */}
        <div className="hidden sm:block absolute top-20 left-10 w-16 h-16 lg:w-20 lg:h-20 bg-white bg-opacity-10 rounded-full animate-bounce"></div>
        <div className="hidden sm:block absolute bottom-20 right-10 w-12 h-12 lg:w-16 lg:h-16 bg-white bg-opacity-10 rounded-full animate-pulse"></div>
        <div className="hidden lg:block absolute top-1/2 left-1/4 w-10 h-10 lg:w-12 lg:h-12 bg-white bg-opacity-10 rounded-full animate-ping"></div>
      </section>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12 lg:py-16">
        {/* Slide Content */}
        <section className={`bg-gradient-to-br ${currentSlideData.bgColor} rounded-3xl p-6 sm:p-8 lg:p-12 mb-12 shadow-xl`}>
          <div className="text-center mb-6 sm:mb-8">
            <p className="text-lg sm:text-xl lg:text-2xl text-gray-700 leading-relaxed mb-6 sm:mb-8">
              {currentSlideData.content.description}
            </p>
          </div>
          
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
            {currentSlideData.content.features.map((feature, index) => (
              <div key={index} className="flex items-center bg-white bg-opacity-60 rounded-xl p-3 sm:p-4 backdrop-blur-sm">
                <CheckCircle className="h-5 w-5 sm:h-6 sm:w-6 text-green-500 mr-3 sm:mr-4 flex-shrink-0" />
                <span className="text-base sm:text-lg font-semibold text-gray-800">{feature}</span>
              </div>
            ))}
          </div>

          {/* Call to Action pour slide de conclusion */}
          {currentSlideData.isConclusion && (
            <div className="text-center mt-8 sm:mt-12">
              <Link to="/office365">
                <Button className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white font-bold px-6 sm:px-8 lg:px-12 py-4 sm:py-6 rounded-full text-lg sm:text-xl shadow-2xl transform hover:scale-105 transition-all duration-300">
                  <Star className="h-6 w-6 sm:h-8 sm:w-8 mr-2 sm:mr-4" />
                  Accéder aux guides détaillés
                  <ArrowRight className="ml-2 sm:ml-4 h-6 w-6 sm:h-8 sm:w-8" />
                </Button>
              </Link>
            </div>
          )}
        </section>

        {/* Navigation Controls */}
        <div className="flex flex-col sm:flex-row items-center justify-between mb-6 sm:mb-8 gap-4">
          <Button
            onClick={prevSlide}
            disabled={currentSlide === 0}
            className={`${
              currentSlide === 0 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:scale-105'
            } bg-gradient-to-r from-gray-600 to-gray-700 text-white font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 text-sm sm:text-base w-full sm:w-auto`}
          >
            <ArrowLeft className="h-4 w-4 sm:h-5 sm:w-5 mr-2" />
            Précédent
          </Button>

          <div className="flex items-center space-x-2 order-first sm:order-none">
            {slides.map((_, index) => (
              <button
                key={index}
                onClick={() => goToSlide(index)}
                className={`w-3 h-3 sm:w-4 sm:h-4 rounded-full transition-all duration-300 ${
                  index === currentSlide
                    ? 'bg-blue-600 scale-125'
                    : 'bg-gray-300 hover:bg-gray-400'
                }`}
              />
            ))}
          </div>

          <Button
            onClick={nextSlide}
            disabled={currentSlide === slides.length - 1}
            className={`${
              currentSlide === slides.length - 1 
                ? 'opacity-50 cursor-not-allowed' 
                : 'hover:scale-105'
            } bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-bold px-4 sm:px-6 py-2 sm:py-3 rounded-full transition-all duration-300 text-sm sm:text-base w-full sm:w-auto`}
          >
            Suivant
            <ArrowRight className="h-4 w-4 sm:h-5 sm:w-5 ml-2" />
          </Button>
        </div>

        {/* Slide Counter */}
        <div className="text-center mb-6 sm:mb-0">
          <div className="inline-flex items-center bg-white rounded-full px-4 sm:px-6 py-2 sm:py-3 shadow-lg">
            <span className="text-base sm:text-lg font-semibold text-gray-700">
              Slide {currentSlide + 1} sur {slides.length}
            </span>
          </div>
        </div>

        {/* Back to Office 365 */}
        <div className="text-center mt-8 sm:mt-12">
          <Link to="/office365">
            <Button className="bg-gradient-to-r from-gray-600 to-gray-700 hover:from-gray-700 hover:to-gray-800 text-white font-bold px-6 sm:px-8 py-3 sm:py-4 rounded-full text-base sm:text-lg shadow-lg transform hover:scale-105 transition-all duration-300 w-full sm:w-auto">
              <Home className="h-5 w-5 sm:h-6 sm:w-6 mr-2 sm:mr-3" />
              Retour à Office 365
            </Button>
          </Link>
        </div>
      </div>
      
      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Office365PresentationPage;